<template>
  <form
    class="grid main-cart critical-component-clear"
    :action="`${$variable('routes.cart.url')}?locale=${$variable('store.language')}`"
    method="post"
  >
    <template v-if="cart.item_count > 0 || cartStatus.label === 'adding'">
      <div
        v-if="$variable('settings.cart.specialtyMessage')"
        class="main-cart__specialty-message col xs-span l1-64"
        v-html="$localizeRichtext($variable('settings.cart.specialtyMessage'))"
      />

      <div class="main-cart__content col xs-span grid no-row-gap">
        <div class="col xs-span l5-35">
          <div class="main-cart__header col xs-span l5-15">
            <h1
              id="template-title"
              class="main-cart__title text-heading-xl text-heading-l-desktop"
              v-html="$string(
                'cart.accessibility.title_html',
                {replace: { count: cart.item_count }, }
              )"
            />
          </div>

          <div class="main-cart__line-items">
            <template
              v-for="(lineItem, index) of cartItems"
              :key="getCartItemKey(lineItem)"
            >
              <line-item
                class="main-cart__line-item"
                :cart-items="cartItems"
                :class="{'last-item': index === cartItems.length - 1}"
                :line-item="lineItem"
                :logged-in="loggedIn"
              />
            </template>
          </div>
        </div>

        <aside class="main-cart__sidebar col xs-span l39-59">
          <div class="main-cart__sidebar-header">
            <h2
              class="main-cart__title text-heading-xl text-heading-l-desktop"
              v-text="$string('cart.payment_summary')"
            />
          </div>

          <cart-summary class="main-cart__summary" />
        </aside>
      </div>
    </template>

    <template v-else>
      <div class="main-cart__header-empty col xs-span l20-44">
        <p
          class=" main-cart__text text-body-m-regular"
          v-html="$string('cart.empty.text_html')"
        />

        <div
          v-if="$variable('settings.cart.emptyCartButton.label') &&
            $variable('settings.cart.emptyCartButton.url')"
          class="main-cart__empty-cta-container"
        >
          <btn
            class="main-cart__empty-cta"
            :label="$variable('settings.cart.emptyCartButton.label')"
            :url="$localizeUrl($variable('settings.cart.emptyCartButton.url'))"
            modifiers="primary center"
          />
        </div>
      </div>
    </template>
  </form>
</template>

<script>

/**
 * Vue: Main cart (main-cart)
 * -----------------------------------------------------------------------------
 * Cart page with line items.
 *
 * @param {Object} empty - Empty cart CTA settings.
 *
 */
import { mapState } from 'vuex'

import Btn from '~global/btn/btn'
import CartSummary from '~async/cart-summary/cart-summary'
import LineItem from '~async/line-item/line-item'

export default {
  name: 'MainCart',

  components: {
    Btn,
    CartSummary,
    LineItem,
  },

  props: {
    loggedIn: {
      type: Boolean,
      default: false,
    },
  },

  computed: {

    /**
     * Map Vuex state.
     */
    ...mapState({
      cart: (state) => state.cart.response,
      cartStatus: (state) => state.cart.status,
    }),

    /**
     * Compute cart items.
     * - For each item that is being added, check if it's already in the cart.
     * - Check against key (if adding from cart) or id (if adding from other).
     * - If it isn't then add a false item to act as a loading placeholder.
     * @returns {Array}
     */
    cartItems() {
      if (this.cartStatus.label !== 'adding') {
        return this.cart.items
      }

      const placeholders = []

      this.cartStatus.items.forEach((addedItem) => {
        const match = this.cart.items.find((cartItem) => {
          const cartItemProperty = typeof addedItem === 'string'
            ? cartItem.key
            : cartItem.id

          return cartItemProperty === addedItem
        })

        if (match) {
          return
        }

        placeholders.push(false)
      })

      return [
        ...placeholders,
        ...this.cart.items,
      ]
    },
  },

  methods: {

    /**
     * Get cart item key.
     * @param {Object} item - Line item.
     * @returns {String}
     */
    getCartItemKey(item) {
      if (!item) {
        return 'cart-line-item-loading'
      }

      return `cart-line-item-${item.key}`
    },
  },
}
</script>

<style lang="scss">
@import './main-cart';
</style>
